import type { Ref } from 'react';

/** Staff sign-in codes are 8 digits (the API reports the length; see lib/auth.ts). */
export const STAFF_CODE_LENGTH = 8;

/** Digits only, at most `length`: a pasted "1234 5678" or "1234-5678" becomes "12345678". */
export function cleanCode(raw: string, length: number = STAFF_CODE_LENGTH): string {
  return raw.replace(/\D/g, '').slice(0, length);
}

/**
 * The emailed sign-in code (ported from the Hype Forge Base, 2026-10-21),
 * `length` digits (8 for staff; at 24 px monospace they still fit a 320 px
 * phone). Numeric keypad on phones, one-time-code autofill, and no maxLength
 * so a pasted code with a space or dash is not cut short before it is
 * cleaned. `onComplete` fires once when the last digit lands, however it got
 * there.
 */
export function CodeInput({
  id,
  value,
  onChange,
  onComplete,
  length = STAFF_CODE_LENGTH,
  busy = false,
  autoFocus = false,
  className,
  describedBy,
  ref,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  onComplete: (code: string) => void;
  /** How many digits the code has. */
  length?: number;
  /** Read-only while a check runs (not disabled, so focus stays put). */
  busy?: boolean;
  autoFocus?: boolean;
  className?: string;
  describedBy?: string;
  ref?: Ref<HTMLInputElement>;
}) {
  return (
    <input
      ref={ref}
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      spellCheck={false}
      autoFocus={autoFocus}
      readOnly={busy}
      className={`code-input${className ? ` ${className}` : ''}`}
      value={value}
      aria-describedby={describedBy}
      onChange={(e) => {
        const next = cleanCode(e.target.value, length);
        onChange(next);
        if (next.length === length && next !== value) onComplete(next);
      }}
    />
  );
}
